import { useQuery } from '@tanstack/react-query'
import { formatMoney, formatMoment, formatQty } from '@shop/shared'
import { BRAND } from '@shop/shared'
import { ErrorBanner, Spinner } from '@shop/ui'
import { Sheet, TouchButton } from '../pos/touch'
import * as pos from '../pos/api'
import type { SaleReceipt } from '../pos/contracts'

/**
 * الفاتورة — <b>تُقرأ وتُطبَع، ولا كلفةَ فيها ولا هامش</b>.
 *
 * ⚠ <b>والخادمُ لا يبنيهما أصلًا</b> — لا تُحجبان هنا: الورقةُ تُسلَّم للزبون،
 *   فلا يوجد طالبٌ تُعرض له كلفتُنا عليها. ويحرس ذلك `ReceiptContractTests`.
 *
 * والطباعةُ <c>window.print()</c> على قالبٍ ضيّق: طابعةُ الإيصالات ٨٠ ملّم،
 * ومتصفّحُ الجهاز هو ما يُرسل إليها.
 */
export default function Receipt({ saleId, onClose }: { saleId: string; onClose: () => void }) {
  // **والفاتورةُ لا تتغيّر بعد الإتمام** — فتُقرأ مرّةً وتبقى.
  const query = useQuery({
    queryKey: ['receipt', saleId],
    queryFn: () => pos.receipt(saleId),
    staleTime: Infinity,
  })

  const receipt: SaleReceipt | null = query.data ?? null

  return (
    <Sheet title="الفاتورة" onClose={onClose}>
      <ErrorBanner error={query.error} />

      {query.isPending && <Spinner />}

      {receipt && (
        <div className="mx-auto max-w-sm">
          <div className="rounded-xl border border-line bg-surface-raised p-4 print:border-0" id="receipt">
            <div className="text-center">
              <p className="font-bold">{BRAND.nameAr}</p>
              <p className="tabular mt-1 text-lg font-bold" dir="ltr">
                {receipt.saleNo}
              </p>
              <p className="text-xs text-ink-soft">{formatMoment(receipt.completedAt)}</p>
              {receipt.sessionNo && (
                <p className="tabular text-xs text-ink-soft" dir="ltr">
                  {receipt.sessionNo}
                </p>
              )}
            </div>

            {receipt.customerName && (
              <p className="mt-3 border-t border-line pt-2 text-sm">
                {receipt.customerName}
                {receipt.customerPhone && (
                  <span className="tabular block text-xs text-ink-soft" dir="ltr">
                    {receipt.customerPhone}
                  </span>
                )}
              </p>
            )}

            <ul className="mt-3 border-t border-line pt-2">
              {receipt.lines.map((line) => (
                <li key={line.lineNo} className="flex items-start justify-between gap-2 py-1 text-sm">
                  <span className="min-w-0">
                    <span className="block truncate">{line.productName}</span>
                    <span className="tabular block text-xs text-ink-soft" dir="ltr">
                      {formatQty(line.qty)} × {formatMoney(line.unitPrice, receipt.currency)}
                      {line.discount > 0 && ` − ${formatMoney(line.discount, receipt.currency)}`}
                    </span>
                  </span>

                  <span className="tabular shrink-0" dir="ltr">
                    {formatMoney(line.lineTotal, receipt.currency)}
                  </span>
                </li>
              ))}
            </ul>

            <dl className="mt-3 border-t border-line pt-2 text-sm">
              <Money label="المجموع" value={receipt.subtotal} currency={receipt.currency} />
              {receipt.discountTotal > 0 && (
                <Money label="الخصم" value={receipt.discountTotal} currency={receipt.currency} />
              )}
              {receipt.taxTotal > 0 && (
                <Money label="الضريبة" value={receipt.taxTotal} currency={receipt.currency} />
              )}
              <Money label="المطلوب" value={receipt.total} currency={receipt.currency} strong />
            </dl>

            <dl className="mt-2 border-t border-line pt-2 text-sm">
              {receipt.payments.map((payment, index) => (
                <Money
                  key={index}
                  label={payment.methodNameAr}
                  value={payment.amount}
                  currency={receipt.currency}
                />
              ))}

              {receipt.change > 0 && (
                <Money label="الفكّة" value={receipt.change} currency={receipt.currency} />
              )}
            </dl>

            <p className="mt-4 text-center text-xs text-ink-soft">شكرًا لزيارتكم</p>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 print:hidden">
            <TouchButton onClick={() => window.print()}>اطبع</TouchButton>
            <TouchButton tone="primary" onClick={onClose}>
              بيعةٌ جديدة
            </TouchButton>
          </div>
        </div>
      )}
    </Sheet>
  )
}

function Money({
  label,
  value,
  currency,
  strong,
}: {
  label: string
  value: number
  currency: string
  strong?: boolean
}) {
  return (
    <div className={`flex items-baseline justify-between gap-3 py-1 ${strong ? 'font-bold' : ''}`}>
      <dt>{label}</dt>
      <dd className="tabular" dir="ltr">
        {formatMoney(value, currency)}
      </dd>
    </div>
  )
}
